import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { calendarApi } from './calendarApi';
import { subscriptionDateLine, subscriptionAction } from './calendarCancelText';

/**
 * The subscriber's own account.
 *
 * REACHABLE WHILE past_due, WHICH IS THE WHOLE POINT. Somebody whose card has
 * failed is exactly the person who most needs this page, so it sits behind
 * authentication but not behind the calendar access gate. A cancelled
 * subscriber reaches it too, and is offered a new subscription rather than an
 * explanation.
 *
 * Every state change here goes to the server, which routes it through
 * lib/calendarSubscribers. Nothing about a subscription is decided in the
 * browser, including whether a cancellation is immediate: that depends on how
 * much of the paid period is left, and the server owns that arithmetic.
 */

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const IS_PRODUCTION = process.env.REACT_APP_SQUARE_ENVIRONMENT === 'production';

// Dates are formatted in calendarCancelText.js, in UTC. The local-zone
// formatter that used to live here showed the previous day to every subscriber
// in the United States, because every period end on this product is computed in
// UTC. Do not reintroduce one.

function Panel({ title, children }) {
  return (
    <section style={{
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      background: 'var(--color-surface)',
      padding: '16px',
      marginBottom: '16px'
    }}>
      <h2 style={{ fontSize: '1rem', color: 'var(--color-heading)', margin: '0 0 12px' }}>{title}</h2>
      {children}
    </section>
  );
}

function CalendarAccount({ liveStates = [], onChanged }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [cardReady, setCardReady] = useState(false);
  const [card, setCard] = useState(null);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await calendarApi('/account');
      setAccount(data.account);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The card form is only mounted when somebody asks to replace a card, so the
  // Square iframe is not loaded on every visit to this page.
  useEffect(() => {
    if (!cardReady || window.Square) return;
    const script = document.createElement('script');
    script.src = IS_PRODUCTION
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => setCardReady(true);
    document.body.appendChild(script);
  }, [cardReady]);

  useEffect(() => {
    const mount = async () => {
      if (!cardReady || !window.Square || !cardRef.current || card) return;
      try {
        const payments = await window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        const instance = await payments.card();
        await instance.attach(cardRef.current);
        setCard(instance);
      } catch {
        setError('The payment form could not start.');
      }
    };
    mount();
  }, [cardReady, card]);

  const act = async (label, path, body) => {
    setBusy(label);
    setError('');
    setNotice('');
    try {
      const data = await calendarApi(path, {
        method: 'POST',
        body: JSON.stringify(body || {})
      });
      setNotice(data.note || 'Saved.');
      await load();
      if (onChanged) await onChanged();
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  const saveCard = async () => {
    if (!card) return;
    setBusy('card');
    setError('');
    setNotice('');
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') {
        setError((result.errors || []).map(e => e.message).join(', ') || 'Please check your card details.');
        return;
      }
      const data = await calendarApi('/account/card', {
        method: 'POST',
        body: JSON.stringify({ cardNonce: result.token })
      });
      setNotice(data.note || 'Your card is saved.');
      setCard(null);
      setCardReady(false);
      await load();
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleDigest = async (enabled) => {
    setBusy('digest');
    try {
      await calendarApi('/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ digestEnabled: enabled })
      });
      await load();
      if (onChanged) await onChanged();
      setNotice(enabled ? 'The weekly email is on.' : 'The weekly email is off.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleState = async (code) => {
    const current = Array.isArray(account.digestStates) ? account.digestStates : [];
    const next = current.includes(code)
      ? current.filter(item => item !== code)
      : [...current, code];
    setBusy('states');
    try {
      await calendarApi('/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ digestStates: next })
      });
      await load();
      setNotice('Saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const changePassword = async (event) => {
    event.preventDefault();
    const data = await act('password', '/account/password', { currentPassword, newPassword });
    if (data) {
      setCurrentPassword('');
      setNewPassword('');
      setNotice('Your password is changed.');
    }
  };

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading your account</p>;
  if (!account) {
    return <p style={{ color: 'var(--color-danger-text, var(--color-text))' }}>{error || 'Could not load your account.'}</p>;
  }

  // One line and one button, both decided in calendarCancelText.js rather than
  // here. The bug this replaces was a page that showed "Next charge" against a
  // date that was the day access ends, which is the same slot saying the
  // opposite thing, so the two sentences are now one function that cannot
  // render both.
  const dateLine = subscriptionDateLine({
    cancelScheduledFor: account.cancelScheduledFor,
    currentPeriodEnd: account.currentPeriodEnd
  });
  const action = subscriptionAction({ cancelScheduledFor: account.cancelScheduledFor });
  const chosenStates = Array.isArray(account.digestStates) ? account.digestStates : null;

  const inputStyle = {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-input-bg, var(--color-surface))',
    color: 'var(--color-text)',
    boxSizing: 'border-box',
    marginBottom: '12px'
  };

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto' }}>
      <h1 style={{ color: 'var(--color-heading)', fontSize: '1.4rem', marginBottom: '16px' }}>Your account</h1>

      {account.status === 'past_due' && (
        <p style={{
          border: '1px solid var(--color-border)',
          borderLeft: '4px solid var(--color-danger, #c0392b)',
          borderRadius: '6px',
          padding: '12px 14px',
          marginBottom: '16px',
          color: 'var(--color-text)'
        }}>
          Your last payment did not go through. Update your card below to keep your calendar.
        </p>
      )}

      {notice && <p style={{ color: 'var(--color-text)', marginBottom: '12px' }}>{notice}</p>}
      {error && <p style={{ color: 'var(--color-danger-text, var(--color-text))', marginBottom: '12px' }}>{error}</p>}

      <Panel title="Subscription">
        <p style={{ margin: '0 0 6px' }}>
          <strong>{account.planLabel}</strong>
        </p>
        <p style={{ margin: '0 0 6px', color: 'var(--color-text-light)' }}>
          Status: {account.status === 'past_due' ? 'Payment overdue' : account.status}
        </p>
        {dateLine && (
          <p style={{ margin: '0 0 12px', color: 'var(--color-text-light)' }}>{dateLine}</p>
        )}

        {/* The server decides this, and it already declines to offer an upgrade
            to somebody on their way out. Square would accept the swap and then
            cancel it at the period end anyway. */}
        {account.canSwitchToAnnual && (
          <button
            className="btn btn-primary"
            style={{ marginRight: '8px', padding: '10px 14px' }}
            disabled={busy === 'plan'}
            onClick={() => act('plan', '/account/plan')}
          >
            {busy === 'plan' ? 'Switching' : 'Switch to annual, $199 per year'}
          </button>
        )}

        {action === 'reactivate' ? (
          <button
            className="btn btn-primary"
            style={{ padding: '10px 14px' }}
            disabled={busy === 'reactivate'}
            onClick={() => act('reactivate', '/account/reactivate')}
          >
            {busy === 'reactivate' ? 'Reactivating' : 'Reactivate'}
          </button>
        ) : (
          <button
            className="btn"
            style={{ padding: '10px 14px', color: 'var(--color-text-muted)' }}
            disabled={busy === 'cancel'}
            onClick={() => act('cancel', '/account/cancel')}
          >
            {busy === 'cancel' ? 'Cancelling' : 'Cancel subscription'}
          </button>
        )}
      </Panel>

      <Panel title="Card">
        <p style={{ margin: '0 0 12px', color: 'var(--color-text-light)' }}>
          {account.cardLast4
            ? `${account.cardBrand || 'Card'} ending ${account.cardLast4}.`
            : 'We cannot read your card details right now.'}
        </p>

        {!cardReady ? (
          <button className="btn" style={{ padding: '10px 14px' }} onClick={() => setCardReady(true)}>
            Update card
          </button>
        ) : (
          <>
            <div
              ref={cardRef}
              style={{
                minHeight: '50px',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px',
                background: 'var(--color-input-bg, var(--color-surface))'
              }}
            >
              {!card && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>Loading the payment form</p>}
            </div>
            <button
              className="btn btn-primary"
              style={{ padding: '10px 14px' }}
              disabled={!card || busy === 'card'}
              onClick={saveCard}
            >
              {busy === 'card' ? 'Saving' : 'Save card'}
            </button>
          </>
        )}
      </Panel>

      <Panel title="Weekly email">
        <label style={{ display: 'block', marginBottom: '12px' }}>
          <input
            type="checkbox"
            checked={Boolean(account.digestEnabled)}
            disabled={busy === 'digest'}
            onChange={e => toggleDigest(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Send me the weekly digest
        </label>

        <p style={{ margin: '0 0 8px', color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
          {chosenStates === null
            ? 'Currently every live state. Pick states to narrow it.'
            : 'Only the states you pick.'}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {liveStates.map(state => {
            const on = chosenStates === null || chosenStates.includes(state.state_code);
            return (
              <button
                key={state.state_code}
                type="button"
                disabled={busy === 'states'}
                onClick={() => toggleState(state.state_code)}
                className={`badge ${on ? 'badge-info' : 'badge-secondary'}`}
                style={{ border: 'none', cursor: 'pointer' }}
              >
                {state.state_code}
              </button>
            );
          })}
        </div>
      </Panel>

      <Panel title="Password">
        <form onSubmit={changePassword}>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Current password"
            style={inputStyle}
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            required
          />
          <input
            type="password"
            autoComplete="new-password"
            placeholder="New password"
            minLength={8}
            style={inputStyle}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
          />
          <button className="btn btn-primary" style={{ padding: '10px 14px' }} disabled={busy === 'password'}>
            {busy === 'password' ? 'Changing' : 'Change password'}
          </button>
        </form>
      </Panel>

      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem', lineHeight: 1.6 }}>
        Cancelling takes effect at the end of the period you have already paid
        for. Partial periods are not refunded. See our{' '}
        <Link to="/terms" style={{ color: 'var(--color-primary)' }}>terms</Link> and{' '}
        <Link to="/privacy" style={{ color: 'var(--color-primary)' }}>privacy policy</Link>.
      </p>
    </div>
  );
}

export default CalendarAccount;
